import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Global wake-word listener.
 *
 * Reports lifecycle via window CustomEvents:
 *   - disciple:wake-status  { state: "idle"|"listening"|"denied"|"unsupported"|"error", detail? }
 *   - disciple:wake         { phrase }  — fired when the wake phrase is heard
 */
export default function WakeListener() {
  const { user } = useAuth();
  const recogRef = useRef(null);
  const pausedRef = useRef(false);
  const mountedRef = useRef(true);
  const [, setTick] = useState(0); // force re-attempt after error

  const enabled = !!user?.wake_enabled;
  const wakePhrase = (user?.wake_word || `hey ${user?.assistant_name || "andre"}`).toLowerCase().trim();

  const status = (state, detail) => {
    window.dispatchEvent(new CustomEvent("disciple:wake-status", { detail: { state, detail } }));
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Pause/resume on overlay open/close
  useEffect(() => {
    const onPause = () => { pausedRef.current = true; try { recogRef.current?.stop(); } catch {} };
    const onResume = () => {
      pausedRef.current = false;
      try { recogRef.current?.start(); } catch {}
    };
    window.addEventListener("disciple:voice-open", onPause);
    window.addEventListener("disciple:voice-close", onResume);
    return () => {
      window.removeEventListener("disciple:voice-open", onPause);
      window.removeEventListener("disciple:voice-close", onResume);
    };
  }, []);

  // Main listener
  useEffect(() => {
    if (!enabled) {
      try { recogRef.current?.stop(); } catch {}
      recogRef.current = null;
      status("idle");
      return;
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      status("unsupported");
      toast.error("Wake word needs Chrome or Edge on desktop. This browser doesn't support background listening.");
      return;
    }

    let stopped = false;
    let r;

    // Pre-flight: ensure mic permission. In some iframes (preview embeds) this
    // is blocked even when the user has granted permission to the host page.
    const startRecognition = () => {
      r = new SR();
      r.continuous = true;
      r.interimResults = true;
      r.lang = "en-US";
      let lastMatchAt = 0;

      r.onstart = () => status("listening");

      r.onresult = (e) => {
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const text = e.results[i][0].transcript.toLowerCase();
          if (text.includes(wakePhrase)) {
            const now = Date.now();
            if (now - lastMatchAt < 4000) return;
            lastMatchAt = now;
            window.dispatchEvent(new CustomEvent("disciple:wake", { detail: { phrase: text } }));
          }
        }
      };

      r.onerror = (ev) => {
        const code = ev.error || "unknown";
        if (code === "no-speech" || code === "aborted") return; // benign
        if (code === "not-allowed" || code === "service-not-allowed") {
          status("denied", code);
          toast.error("Microphone permission denied. Wake word can't listen in this browser/iframe.");
          stopped = true;
          try { r.stop(); } catch {}
          return;
        }
        status("error", code);
      };

      r.onend = () => {
        if (!mountedRef.current || pausedRef.current || stopped) return;
        try { r.start(); } catch {}
      };

      recogRef.current = r;
      try {
        r.start();
      } catch (e) {
        status("error", String(e?.message || e));
      }
    };

    // Ask for microphone permission explicitly so we get a clear error when blocked.
    (async () => {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((t) => t.stop()); // release immediately
        }
      } catch (err) {
        status("denied", String(err?.name || err));
        toast.error(
          "Microphone is blocked. If you're inside a preview iframe, open the app in a new tab and allow mic to use wake word."
        );
        return;
      }
      if (!mountedRef.current) return;
      startRecognition();
    })();

    return () => {
      stopped = true;
      try { recogRef.current?.stop(); } catch {}
      recogRef.current = null;
    };
  }, [enabled, wakePhrase, setTick]);  // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
